/**
 * Tenant Report Service
 * Generate / list / view / download tenant usage reports (management-scoped).
 */

import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

export type ReportTimeRange = 'week' | 'month' | 'quarter' | 'year' | 'custom';

export interface TenantReportRecord {
  id: string;
  tenant_key: string;
  tenant_name?: string;
  time_range?: string;
  period_start: string;
  period_end: string;
  generated_by?: string;
  generated_by_email?: string;
  status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED';
  started_at?: string;
  finished_at?: string;
  duration_ms?: number;
  error?: string;
  file_url?: string;
  file_name?: string;
  createdAt: string;
}

export interface GenerateReportPayload {
  tenant_key: string;
  tenant_name?: string;
  time_range?: ReportTimeRange;
  start_date?: string;
  end_date?: string;
}

export interface ListReportsParams {
  skip?: number;
  limit?: number;
  tenant_key?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
}

export interface PaginatedReports {
  items: TenantReportRecord[];
  total: number;
  skip: number;
  limit: number;
  totalPages: number;
}

class TenantReportService {
  /** Lightweight tenant list for the dropdown — only { tenant_key, name }. */
  async listTenants(): Promise<{ tenant_key: string; name: string }[]> {
    const response = await apiClient.get<{ success: boolean; data: { tenant_key: string; name: string }[] }>(
      API_ENDPOINTS.TENANT_REPORTS.TENANTS,
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  /** Generate + store a report for one tenant. Returns the created record. */
  async generate(payload: GenerateReportPayload): Promise<TenantReportRecord> {
    const response = await apiClient.post<{ success: boolean; data: TenantReportRecord }>(
      API_ENDPOINTS.TENANT_REPORTS.GENERATE,
      payload,
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  /** Paginated history of generated reports (table). */
  async list(params: ListReportsParams = {}): Promise<PaginatedReports> {
    const response = await apiClient.get<{ success: boolean; data: PaginatedReports }>(
      API_ENDPOINTS.TENANT_REPORTS.LIST,
      { params },
    );
    if (!response.data.success) throw response.data;
    return response.data.data;
  }

  /** Get the public S3 URL of the report (View — opens in the browser). */
  async getUrl(id: string): Promise<string> {
    const response = await apiClient.get<{ success: boolean; data: { url: string } }>(
      API_ENDPOINTS.TENANT_REPORTS.URL(id),
    );
    if (!response.data.success) throw response.data;
    return response.data.data.url;
  }

  /**
   * Download the report file as a Blob — streamed through the authenticated
   * backend (reliable; a direct cross-origin S3 link can't force a download).
   */
  async downloadBlob(id: string, silent = false): Promise<Blob> {
    // silent=true → the auto-download right after Generate; the backend then skips the
    // "Downloaded" audit so Generate logs only once. Explicit downloads omit it (audited).
    const response = await apiClient.get<Blob>(
      API_ENDPOINTS.TENANT_REPORTS.DOWNLOAD(id),
      { responseType: 'blob', params: silent ? { silent: 'true' } : {} },
    );
    return response.data;
  }
}

export default new TenantReportService();
